import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from './config';
import { optimizeImage, convertToWebP } from '@/lib/utils/imageOptimization';
import type { ImageMenuItem, ImageMenuDisplayConfig } from '@/types';

// Maximum number of images per restaurant
export const MAX_IMAGE_MENU_ITEMS = 50;

interface ImageMetadata {
  fileName: string;
  fileSize: number;
  dimensions: { width: number; height: number };
  originalSize: number;
}

/**
 * Upload an image to Firebase Storage for image menu
 * Creates both full-size and thumbnail versions in WebP format
 * Maintains original aspect ratio (no cropping)
 */
export async function uploadImageMenuPhoto(
  restaurantId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ url: string; thumbnailUrl: string; metadata: ImageMetadata }> {
  try {
    // Generate unique file ID
    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Always use .webp extension for optimized storage
    const fileName = `${fileId}.webp`;
    
    // Compress main image (max 1200x1200, 85% quality) - maintains aspect ratio
    const compressedImage = await optimizeImage(file, {
      maxDimensions: { width: 1200, height: 1200 },
      compressionQuality: 1,
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'] // Accept original types
    });

    // Create thumbnail (max 400x400, 80% quality) - maintains aspect ratio  
    const thumbnail = await optimizeImage(file, {
      maxDimensions: { width: 400, height: 400 },
      compressionQuality: 0.80,
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp']
    });
    
    // Convert both to WebP format (without cropping)
    const webpMainImage = await convertToWebP(compressedImage.file, 0.85);
    const webpThumbnail = await convertToWebP(thumbnail.file, 0.80);
    
    // Upload main image
    const mainImageRef = ref(storage, `restaurants/${restaurantId}/image-menu/${fileName}`);
    const mainImageSnapshot = await uploadBytes(mainImageRef, webpMainImage);
    const mainImageUrl = await getDownloadURL(mainImageSnapshot.ref);
    
    // Upload thumbnail
    const thumbnailRef = ref(storage, `restaurants/${restaurantId}/image-menu/thumbnails/${fileName}`);
    const thumbnailSnapshot = await uploadBytes(thumbnailRef, webpThumbnail);
    const thumbnailUrl = await getDownloadURL(thumbnailSnapshot.ref);
    
    if (onProgress) {
      onProgress(100);
    }
    
    return {
      url: mainImageUrl,
      thumbnailUrl,
      metadata: {
        fileName: file.name,
        fileSize: webpMainImage.size,
        dimensions: compressedImage.dimensions,
        originalSize: file.size
      }
    };
  } catch (error) {
    console.error('Error uploading image menu photo:', error);
    throw new Error('Failed to upload image. Please try again.');
  }
}/**
 * Add a new image menu item to Firestore
 */
export async function addImageMenuItem(
  restaurantId: string,
  imageData: {
    imageUrl: string;
    thumbnailUrl: string;
    fileName: string;
    fileSize: number;
    dimensions: { width: number; height: number };
  },
  currentImageCount: number = 0
): Promise<ImageMenuItem> {
  try {
    // Check if we've reached the limit
    if (currentImageCount >= MAX_IMAGE_MENU_ITEMS) {
      throw new Error(`Maximum ${MAX_IMAGE_MENU_ITEMS} images allowed per restaurant`);
    }
    
    const docData = {
      restaurantId,
      imageUrl: imageData.imageUrl,
      thumbnailUrl: imageData.thumbnailUrl,
      fileName: imageData.fileName,
      fileSize: imageData.fileSize,
      dimensions: imageData.dimensions,
      order: currentImageCount, // Use passed count instead of fetching
      uploadedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'imageMenuItems'), docData);
    
    return {
      id: docRef.id,
      ...docData,
      uploadedAt: new Date(),
      updatedAt: new Date()
    } as ImageMenuItem;
  } catch (error) {
    console.error('Error adding image menu item:', error);
    throw error;
  }
}

/**
 * Get all image menu items for a restaurant
 */
export async function getImageMenuItems(restaurantId: string): Promise<ImageMenuItem[]> {
  try {
    const q = query(
      collection(db, 'imageMenuItems'),
      where('restaurantId', '==', restaurantId)
    );
    
    const querySnapshot = await getDocs(q);
    
    const items = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        uploadedAt: data.uploadedAt instanceof Timestamp ? data.uploadedAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
      } as ImageMenuItem;
    });
    
    // Sort by order in memory
    return items.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error fetching image menu items:', error);
    throw new Error('Failed to load image menu items');
  }
}/**
 * Delete an image menu item
 */
export async function deleteImageMenuItem(imageMenuId: string, imageUrl: string, thumbnailUrl?: string): Promise<void> {
  try {
    // Delete from Firestore
    await deleteDoc(doc(db, 'imageMenuItems', imageMenuId));
    
    // Delete images from Storage
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.warn('Failed to delete main image from storage:', error);
    }
    
    if (thumbnailUrl) {
      try {
        const thumbnailRef = ref(storage, thumbnailUrl);
        await deleteObject(thumbnailRef);
      } catch (error) {
        console.warn('Failed to delete thumbnail from storage:', error);
      }
    }
  } catch (error) {
    console.error('Error deleting image menu item:', error);
    throw new Error('Failed to delete image');
  }
}

/**
 * Update the order of image menu items
 */
export async function reorderImageMenuItems(items: ImageMenuItem[]): Promise<void> {
  try {
    const updatePromises = items.map((item, index) => 
      updateDoc(doc(db, 'imageMenuItems', item.id), {
        order: index,
        updatedAt: serverTimestamp()
      })
    );
    
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error reordering image menu items:', error);
    throw new Error('Failed to reorder images');
  }
}

/**
 * Get image menu display configuration based on digital menu availability
 */
export function getImageMenuDisplayConfig(
  hasImageMenu: boolean,
  hasDigitalMenu: boolean,
  hasAvailableDigitalItems: boolean
): ImageMenuDisplayConfig {
  // If only image menu is available OR all digital menu items are disabled
  const shouldShowGridDirectly = hasImageMenu && (!hasDigitalMenu || !hasAvailableDigitalItems);
  
  // Show thumbnail row only when both image and digital menus are available and active
  const shouldShowThumbnailRow = hasImageMenu && hasDigitalMenu && hasAvailableDigitalItems;
  
  return {
    showThumbnailRow: shouldShowThumbnailRow,
    thumbnailCount: { mobile: 2, desktop: 2 },
    showGridDirectly: shouldShowGridDirectly,
    hasDigitalMenu,
    hasAvailableDigitalItems
  };
}